import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/*
  Zadanie optymalizacyjne: wartość ekstremalna funkcji kwadratowej
  (czyli y_w – współrzędna y wierzchołka)

  Dla f(x) = ax^2 + bx + c:
  - jeśli a > 0 -> minimum w wierzchołku, y_w = f(x_w)
  - jeśli a < 0 -> maksimum w wierzchołku, y_w = f(x_w)

  Metoda główna:
    1) x_w = -b/(2a)
    2) y_w = f(x_w)

  Dodatkowo (w kroku alternatywnym):
    y_w = -Δ/(4a), gdzie Δ = b^2 - 4ac

  Losowanie delty w proporcjach 1:3:1 (Δ>0 : Δ=0 : Δ<0).
*/

/* =========================
   Helpers
========================= */

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickByWeight(items) {
  const total = items.reduce((s, it) => s + (it.w ?? 1), 0);
  let r = Math.random() * total;
  for (const it of items) {
    r -= (it.w ?? 1);
    if (r <= 0) return it.v;
  }
  return items[items.length - 1].v;
}

// FractionInput: akceptujemy liczby, ułamki, przecinki
function parseNumber(rawValue) {
  if (rawValue == null) return null;

  let raw =
    typeof rawValue === "object" && rawValue.raw != null
      ? String(rawValue.raw)
      : String(rawValue);

  raw = raw.trim().replace(/\s+/g, "").replace(",", ".").replace(/\u2212/g, "-");
  if (raw === "") return null;

  // ułamek p/q
  const frac = raw.match(/^([+\-]?\d+)\s*\/\s*([+\-]?\d+)$/);
  if (frac) {
    const p = parseInt(frac[1], 10);
    const q = parseInt(frac[2], 10);
    if (!Number.isFinite(p) || !Number.isFinite(q) || q === 0) return null;
    return p / q;
  }

  // liczba dziesiętna / całkowita
  if (/^[+\-]?\d+(\.\d+)?$/.test(raw)) {
    const v = parseFloat(raw);
    return Number.isFinite(v) ? v : null;
  }

  return null;
}

/* =========================
   Ładny zapis wielomianu (bez 1x i -1x)
========================= */

function termAx2(a) {
  if (a === 1) return "x^2";
  if (a === -1) return "-x^2";
  return `${a}x^2`;
}

function termBx(b) {
  if (b === 0) return null;
  const abs = Math.abs(b);
  const core = abs === 1 ? "x" : `${abs}x`;
  return { sign: b > 0 ? "+" : "-", core };
}

function termC(c) {
  if (c === 0) return null;
  return { sign: c > 0 ? "+" : "-", core: String(Math.abs(c)) };
}

function wrapInt(n) {
  return n < 0 ? `(${n})` : String(n);
}

/* =========================
   Generator (Δ w proporcjach 1:3:1)
========================= */

const DELTA_CASES = [
  { v: "POS", w: 1 },  // Δ > 0
  { v: "ZERO", w: 3 }, // Δ = 0
  { v: "NEG", w: 1 },  // Δ < 0
];

export function generateTask() {
  const deltaCase = pickByWeight(DELTA_CASES);

  // a != 0; chcemy czasem min, czasem max
  const aOptions = [-3, -2, -1, 1, 2, 3];
  const a = aOptions[randInt(0, aOptions.length - 1)];

  // chcemy „ładny” x_w = k (całkowity)
  const k = randInt(-6, 6) || 2;
  const b = -2 * a * k;

  // Wymuszamy znak delty: Δ = 4a*t
  // wtedy c = a k^2 - t i Δ = b^2 - 4ac = 4a*t
  let t = 0;
  if (deltaCase === "ZERO") {
    t = 0;
  } else {
    const absT = randInt(1, 10);
    const wantSameSignAsA = deltaCase === "POS";
    const signT = wantSameSignAsA ? Math.sign(a) : -Math.sign(a);
    t = signT * absT;
  }

  const c = a * k * k - t;

  const ask = a > 0 ? "min" : "max";
  const xW = k;

  // yW = f(k) = a k^2 + b k + c
  const yW = a * k * k + b * k + c;

  // delta do kroku alternatywnego
  const delta = b * b - 4 * a * c;

  return {
    type: "QuadraticExtremeValueTask",
    data: {
      a,
      b,
      c,
      ask,
      xW,
      yW,
      delta,
      maxPoints: 1,
    },
  };
}

/* =========================
   Treść
========================= */

export function renderTask({ data }) {
  const { a, b, c, ask } = data;

  const bx = termBx(b);
  const cc = termC(c);

  const parts = [termAx2(a)];
  if (bx) parts.push(`${bx.sign} ${bx.core}`);
  if (cc) parts.push(`${cc.sign} ${cc.core}`);

  const polyLatex = parts.join(" ");

  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p><strong>Zadanie optymalizacyjne.</strong></p>

      <p>
        Dana jest funkcja <InlineMath math={"f"} /> określona wzorem:
      </p>

      <BlockMath math={`f(x) = ${polyLatex}.`} />

      <p>
        Oblicz{" "}
        {ask === "min" ? "najmniejszą" : "największą"}{" "}
        wartość tej funkcji.
      </p>
    </div>
  );
}

/* =========================
   Input
========================= */

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};
  return (
    <div style={{ display: "grid", rowGap: 10, maxWidth: 360 }}>
      <div>
        Podaj tę wartość (czyli <InlineMath math={"y_w"} />):
      </div>
      <FractionInput
        value={ua.yW}
        onChange={(v) => onChange({ ...(ua || {}), yW: v })}
        disabled={disabled}
        label="y_w"
      />
    </div>
  );
}

/* =========================
   Podgląd odpowiedzi
========================= */

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;

  const raw =
    typeof userAnswer.yW === "object"
      ? userAnswer.yW?.raw ?? ""
      : String(userAnswer.yW ?? "");

  const latex = raw || "\\text{–}";

  return (
    <div>
      <InlineMath math={`y_w = ${latex}`} />
    </div>
  );
}

/* =========================
   Check
========================= */

export function checkAnswer({ data }, userAnswer) {
  const v = parseNumber(userAnswer?.yW);
  if (v == null) return false;
  return Math.abs(v - data.yW) < 1e-9;
}

/* =========================
   Poprawna odpowiedź
========================= */

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left" }}>
      <InlineMath math={`y_w = ${data.yW}`} />
    </div>
  );
}

/* =========================
   Wyjaśnienie
========================= */

export function renderExplanation({ data }) {
  const { a, b, c, ask, xW, yW, delta } = data;

  const aW = wrapInt(a);
  const bW = wrapInt(b);
  const cW = wrapInt(c);

  const whyText =
    ask === "min" ? (
      <>
        Ponieważ <InlineMath math={"a>0"} />, parabola ma ramiona skierowane w górę,
        więc wierzchołek odpowiada <strong>najmniejszej</strong> wartości funkcji.
      </>
    ) : (
      <>
        Ponieważ <InlineMath math={"a<0"} />, parabola ma ramiona skierowane w dół,
        więc wierzchołek odpowiada <strong>największej</strong> wartości funkcji.
      </>
    );

  const stepX1 = String.raw`x_w=-\dfrac{b}{2a}.`;
  const stepX2 = String.raw`x_w=-\dfrac{${bW}}{2\cdot ${aW}}=${xW}.`;

  const stepY1 = String.raw`y_w=f(x_w).`;
  const stepY2 = String.raw`y_w=f(${xW})=${aW}\cdot(${xW})^2+${bW}\cdot(${xW})+${cW}=${yW}.`;

  // alternatywnie z deltą (w details)
  const stepD1 = String.raw`\Delta=b^2-4ac=${bW}^2-4\cdot ${aW}\cdot ${cW}=${delta}.`;
  const stepD2 = String.raw`y_w=-\dfrac{\Delta}{4a}=-\dfrac{${delta}}{4\cdot ${aW}}=${yW}.`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7, marginTop: 8 }}>
      <div style={{ marginBottom: 8 }}>
        <strong>Krok 1.</strong> {whyText}
      </div>

      <div style={{ marginBottom: 8 }}>
        <strong>Krok 2.</strong> Liczymy współrzędną <InlineMath math={"x_w"} /> wierzchołka:
      </div>
      <BlockMath math={stepX1} />
      <BlockMath math={stepX2} />

      <div style={{ marginBottom: 8 }}>
        <strong>Krok 3.</strong> Podstawiamy do funkcji i liczymy współrzędną <InlineMath math={"y_w"} />:
      </div>
      <BlockMath math={stepY1} />
      <BlockMath math={stepY2} />

      <div style={{ marginTop: 8 }}>
        <strong>Wniosek.</strong> {ask === "min" ? "Najmniejsza" : "Największa"} wartość wynosi{" "}
        <InlineMath math={`y_w=${yW}`} />.
      </div>

      <details style={{ marginTop: 12 }}>
        <summary style={{ cursor: "pointer" }}>
          Dodatkowo (inny sposób): wzór z deltą
        </summary>

        <div style={{ marginTop: 8 }}>
          <div style={{ marginBottom: 8 }}>
            Można też skorzystać ze wzoru{" "}
            <InlineMath math={"y_w=-\\dfrac{\\Delta}{4a}"} />, gdzie{" "}
            <InlineMath math={"\\Delta=b^2-4ac"} />.
          </div>

          <BlockMath math={stepD1} />
          <BlockMath math={stepD2} />
        </div>
      </details>
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
